import {
  useMyBubblesQuery,
  useRoomMembersQuery,
  useRoomRequestsQuery,
  useRoomSummaryQuery,
} from "@crowdcoursing/api/graphql";
import {
  type CreateEventRequest,
  useEventCreateEvent,
  useRoomAddFacilitator,
  useRoomAddToRoom,
  useRoomAddToRoomByEmail,
  useRoomCancelRequestAddToRoom,
  useRoomChangeRoomOwnership,
  useRoomDeleteFacilitator,
  useRoomDeleteRoom,
  useRoomDenyRequest,
  useRoomRemoveFromRoom,
  useRoomRequestAddToRoom,
  useRoomUpdateRoom,
} from "@crowdcoursing/api/openapi";
import { useState } from "react";

import { Route } from "~services/router/Route";
import { browserRouter } from "~services/router/router";
import { valueOrFallback } from "~utils/sanitizeDto";
import { makeRoomUrl } from "~utils/url";

import { useRoomAvatar } from "./useRoomAvatar";
import { useRoomMilestoneManagement } from "./useRoomMilestoneManagement";
import { useRoomResourceManagement } from "./useRoomResourceManagement";
import { useRoomTaskManagement } from "./useRoomTaskManagement";
import { useToast } from "./useToast";
import { useWrappedChat } from "./useWrappedChat";

export interface UseRoomDashboardProps {
  roomId: string;
  userId: string;
}

export const useRoomDashboard = ({ roomId, userId }: UseRoomDashboardProps) => {
  const toast = useToast();
  const [pendingRemovalMember, setPendingRemovalMember] = useState<{ id: string; name: string } | undefined>();

  // Get room data
  const {
    data: room,
    isLoading,
    error,
    refetch: refetchRoom,
  } = useRoomSummaryQuery(
    { id: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params") },
    { select: (data) => data.room },
  );

  // Get members data separately
  const {
    data: memberData,
    isLoading: membersLoading,
    refetch: refetchMembers,
  } = useRoomMembersQuery(
    { id: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params") },
    {
      select: (data) => {
        // Get owner ID
        const ownerId = data.room?.owner?.id;

        // Create facilitator set, ensuring owner is included if they exist
        const facilitatorIds = new Set(data.room?.facilitators?.map((f) => f.id) ?? []);
        if (ownerId && !facilitatorIds.has(ownerId)) {
          facilitatorIds.add(ownerId);
        }

        return {
          members: (data.room?.members ?? []).map((member) => ({
            ...member,
            isFacilitator: facilitatorIds.has(member.id),
            // Owner is always a facilitator
            isOwner: member.id === ownerId,
          })),
        };
      },
    },
  );

  // Determine if user can see requests (must be facilitator or owner)
  const canSeeRequests = Boolean(
    !isLoading && !membersLoading && room?.id && memberData?.members && (room.userIsFacilitator || room.userIsOwner),
  );

  // Get requests data separately - only if user is a facilitator and room data is loaded
  const {
    data: requestsData,
    isLoading: requestsLoading,
    refetch: refetchRequests,
  } = useRoomRequestsQuery(
    { id: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params") },
    {
      enabled: canSeeRequests,
      select: (data) => ({
        requests: data.room?.requests ?? [],
      }),
    },
  );

  // Get bubble data for event creation
  const { data: bubblesData } = useMyBubblesQuery(
    {},
    {
      select: (data) => ({
        bubbles: data.me.bubbles.map((bubble) => ({
          id: bubble.id,
          value: bubble.name,
        })),
      }),
    },
  );

  // Initialize management hooks
  const refetchAll = () => {
    refetchRoom();
    refetchMembers();
    refetchRequests();
  };

  const resourceManagement = useRoomResourceManagement(
    valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
    refetchAll,
  );

  const taskManagement = useRoomTaskManagement(
    valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
    valueOrFallback(userId, "MISSING_USER_ID", "Missing userId"),
    refetchAll,
  );

  const milestoneManagement = useRoomMilestoneManagement(
    valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
    refetchAll,
  );

  // Get chat member count
  const { members: chatMembers } = useWrappedChat({
    roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
  });

  const { imageUrl, status, uploadImage } = useRoomAvatar(roomId);

  // Add member mutation
  const { mutateAsync: addMemberByEmail } = useRoomAddToRoomByEmail();

  // Remove member mutation
  const { mutateAsync: removeMemberFromRoom } = useRoomRemoveFromRoom();

  // Facilitator management mutations
  const { mutateAsync: addFacilitator } = useRoomAddFacilitator();
  const { mutateAsync: deleteFacilitator } = useRoomDeleteFacilitator();

  // Room membership mutations
  const { mutateAsync: requestJoinRoom } = useRoomRequestAddToRoom();
  const { mutateAsync: leaveRoom } = useRoomRemoveFromRoom();
  const { mutateAsync: addMemberToRoom } = useRoomAddToRoom();
  const { mutateAsync: denyRoomRequest } = useRoomDenyRequest();
  const { mutateAsync: cancelRoomRequest } = useRoomCancelRequestAddToRoom();

  // Event creation mutation
  const { mutateAsync: createEvent } = useEventCreateEvent();

  // Room deletion mutation
  const { mutateAsync: deleteRoom } = useRoomDeleteRoom({
    onSuccess: () => {
      toast.success("Room deleted successfully!");
      browserRouter.navigate(Route.home());
    },
  });

  // Room update mutation
  const { mutateAsync: updateRoom } = useRoomUpdateRoom({
    onSuccess: () => {
      toast.success("Room updated successfully!");
      refetchAll();
    },
  });

  const handleJoinRoom = async () => {
    if (!userId) {
      toast.error("You must be logged in to join a room");
      return;
    }

    await requestJoinRoom(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId,
        },
      },
      {
        onError: (error) => {
          console.error("Error joining room:", error);
          toast.error("Failed to join room. Please try again.");
        },
        onSuccess: () => {
          if (room?.isPublic) {
            toast.success("You have joined the room!");
          } else {
            toast.success("Join request sent!");
          }
          refetchAll(); // Refresh room data
        },
      },
    );
  };

  const handleLeaveRoom = async () => {
    await leaveRoom(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId,
        },
      },
      {
        onError: (error) => {
          console.error("Error leaving room:", error);
          toast.error("Failed to leave room. Please try again.");
        },
        onSuccess: () => {
          toast.success("You have left the room");
          refetchAll(); // Refresh room data
        },
      },
    );
  };

  const handleCancelRequest = async () => {
    await cancelRoomRequest(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId,
        },
      },
      {
        onError: (error) => {
          console.error("Error canceling room request:", error);
          toast.error("Failed to cancel request. Please try again.");
        },
        onSuccess: () => {
          refetchAll(); // Refresh room data
        },
      },
    );
  };

  const handleShareRoom = () => {
    if (!room) {
      return;
    }
    const sharableRoomLink = makeRoomUrl(room.id);
    navigator.clipboard.writeText(sharableRoomLink);
    toast.success("Room link copied to clipboard!");
  };

  const handleAddMember = async (member: { firstName: string; lastName: string; email: string }) => {
    await addMemberByEmail(
      {
        body: {
          email: member.email,
          firstName: member.firstName,
          lastName: member.lastName,
        },
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
        },
      },
      {
        onError: (error) => {
          console.error("Error adding member:", error);
          toast.error("Failed to add member. Please try again.");
        },
        onSuccess: () => {
          toast.success("Member invitation sent successfully!");
          refetchAll(); // Refresh member data
        },
      },
    );
  };

  const handleRemoveMember = (memberId: string) => {
    // Find the member to get their name for the confirmation dialog
    const member = memberData?.members.find((m) => m.id === memberId);
    if (member) {
      setPendingRemovalMember({
        id: memberId,
        name: `${member.firstName} ${member.lastName}`,
      });
    }
  };

  const confirmRemoveMember = async (shouldRemove = false) => {
    if (!pendingRemovalMember || !shouldRemove) {
      setPendingRemovalMember(undefined);
      return;
    }

    await removeMemberFromRoom(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId: pendingRemovalMember.id,
        },
      },
      {
        onError: (error) => {
          console.error("Error removing member:", error);
          toast.error("Failed to remove member. Please try again.");
        },
        onSettled: () => {
          setPendingRemovalMember(undefined);
        },
        onSuccess: () => {
          toast.success("Member removed successfully!");
          refetchAll(); // Refresh member data
        },
      },
    );
  };

  const handleApproveMember = async (memberId: string) => {
    await addMemberToRoom(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId: memberId,
        },
      },
      {
        onError: (error) => {
          console.error("Error approving member:", error);
          toast.error("Failed to approve member request. Please try again.");
        },
        onSuccess: () => {
          toast.success("Member request approved successfully!");
          refetchAll(); // Refresh member data
        },
      },
    );
  };

  const handleRejectMember = async (memberId: string) => {
    await denyRoomRequest(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId: memberId,
        },
      },
      {
        onError: (error) => {
          console.error("Error rejecting member:", error);
          toast.error("Failed to reject member request. Please try again.");
        },
        onSuccess: () => {
          toast.success("Member request rejected successfully!");
          refetchAll(); // Refresh member data
        },
      },
    );
  };

  const handleAddFacilitator = async (memberId: string) => {
    await addFacilitator(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId: memberId,
        },
      },
      {
        onError: (error) => {
          console.error("Error promoting to facilitator:", error);
          toast.error("Failed to promote member to facilitator. Please try again.");
        },
        onSuccess: () => {
          toast.success("Member promoted to facilitator successfully!");
          refetchAll(); // Refresh member data
        },
      },
    );
  };

  const handleRemoveFacilitator = async (memberId: string) => {
    await deleteFacilitator(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId: memberId,
        },
      },
      {
        onError: (error) => {
          console.error("Error demoting facilitator:", error);
          toast.error("Failed to demote facilitator. Please try again.");
        },
        onSuccess: () => {
          toast.success("Facilitator demoted to member successfully!");
          refetchAll(); // Refresh member data
        },
      },
    );
  };

  // Change ownership mutation
  const { mutateAsync: changeOwnership } = useRoomChangeRoomOwnership();

  const handleAddOwner = async (memberId: string) => {
    await changeOwnership(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
          userId: memberId,
        },
      },
      {
        onError: (error) => {
          console.error("Error changing room ownership:", error);
          toast.error("Failed to transfer room ownership. Please try again.");
        },
        onSuccess: () => {
          toast.success("Room ownership transferred successfully!");
          refetchAll(); // Refresh all data
        },
      },
    );
  };

  // Event creation handler
  const handleAddEvent = async (event: {
    bubbleId: string;
    description: string;
    isAutoJoin: boolean;
    isSearchable: boolean;
    name: string;
  }) => {
    const eventData: CreateEventRequest = {
      bubbleId: event.bubbleId,
      description: event.description,
      isPublic: event.isAutoJoin,
      // Map isAutoJoin to isPublic
      isSearchable: event.isSearchable,
      name: event.name,
      roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"), // No rules for simplified modal
    };

    await createEvent(
      {
        body: eventData,
      },
      {
        onError: (error) => {
          console.error("Error creating event:", error);
          toast.error("Failed to create event. Please try again.");
        },
        onSuccess: () => {
          toast.success("Event created successfully!");
          refetchAll(); // Refresh room data to show new event
        },
      },
    );
  };

  const handleDeleteRoom = async () => {
    await deleteRoom(
      {
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
        },
      },
      {
        onError: (error) => {
          console.error("Error deleting room:", error);
          toast.error("Failed to delete room. Please try again.");
        },
      },
    );
  };

  const handleUpdateRoom = async (values: {
    bubbleId: string;
    description: string;
    isPublic: boolean;
    isSearchable: boolean;
    name: string;
  }) => {
    await updateRoom(
      {
        body: {
          description: values.description,
          facilitatorId: userId,
          isPublic: values.isPublic,
          isSearchable: values.isSearchable,
          name: values.name,
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
        },
        pathParams: {
          roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "Missing roomId in url params"),
        },
      },
      {
        onError: (error) => {
          console.error("Error updating room:", error);
          toast.error("Failed to update room. Please try again.");
        },
      },
    );
  };

  return {
    bubblesData,
    chatMembers,
    confirmRemoveMember,
    error,
    handleAddEvent,
    handleAddFacilitator,
    handleAddMember,
    handleAddOwner,
    handleApproveMember,
    handleCancelRequest,
    handleDeleteRoom,
    handleJoinRoom,
    handleLeaveRoom,
    handleRejectMember,
    handleRemoveFacilitator,
    handleRemoveMember,
    handleShareRoom,
    handleUpdateRoom,
    imageUrl,
    isLoading: isLoading || membersLoading || requestsLoading,
    memberData,
    milestoneManagement,
    pendingRemovalMember,
    requestsData,
    resourceManagement,
    room,
    status,
    taskManagement,
    uploadImage,
  };
};
